import React, { useEffect, useState } from "react";
import Header from "../../../../Componets/Header";
import Table from "../../../../Componets/Table";
import Loadercomponent from "../../../../Componets/Loader";
import { get_ar_report, get_customer_invoices } from "../../../../Controllers/Accounting/Services/ArReport";
import toast from "react-hot-toast";
import tables from "../../../../Data/Tables/Accounting/ReportEntry/ARReport/tables";
import headers from "../../../../Data/Tables/Accounting/ReportEntry/ARReport/headers";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button, Modal, Table as AntTable, Tag } from "antd";
import { EyeOutlined, FileOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const ArReportTable = () => {
  const [loader, setLoader] = useState(false);
  const [header, setHeader] = useState(headers);
  const [body, setBody] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalCount: 0,
    totalPages: 0,
  });
  
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  
  const navigate = useNavigate();

  const paginationdata = (data) => {
    let headerdata = {};
    header?.components?.map((value) => {
      headerdata[value.name] = value.value;
    });
    fetchData({
      ...data,
      date: headerdata?.date,
      customer: headerdata?.customer,
    });
  };

  const formdata = (data) => {
    let page = data?.search ? pagination?.currentPage : 1;
    fetchData({
      ...data,
      currentPage: page,
      totalCount: pagination?.totalCount,
      totalPages: pagination?.totalPages,
    });
  };

  const fetchData = async (tabledata) => {
    try {
      setLoader(true);

      let from, to;
      if (tabledata?.date?.start && tabledata?.date?.end) {
        from = dayjs(tabledata.date.start).format("YYYY-MM-DD");
        to = dayjs(tabledata.date.end).format("YYYY-MM-DD");
      } 

      const response = await get_ar_report({
        from: from,
        to: to,
        customer: tabledata?.customer,
      });

      if (response?.success) {
        const arData = response.data || [];

        const tableBody = arData.map((item, index) => ({
          key: item.customer_id || index,
          "Customer": (
            <div>
              <div className="font-semibold">{item.customer_name}</div>
              <div className="text-sm text-gray-500">{item.customer_phone || "No Phone"}</div>
            </div>
          ),
          "Total Receivable": (
            <span className="font-semibold text-blue-600">
              {parseFloat(item.total_receivable || 0).toFixed(3)}
            </span>
          ),
          "Outstanding": (
            <span className="font-semibold text-red-600">
              {parseFloat(item.outstanding || 0).toFixed(3)}
            </span>
          ),
          "0-30 Days": parseFloat(item.age_0_30 || 0).toFixed(3),
          "31-60 Days": parseFloat(item.age_31_60 || 0).toFixed(3),
          "61-90 Days": parseFloat(item.age_61_90 || 0).toFixed(3),
          "90+ Days": (
            <span className={item.age_90_plus > 0 ? "text-red-600 font-semibold" : ""}>
              {parseFloat(item.age_90_plus || 0).toFixed(3)}
            </span>
          ),
          "Invoice Count": (
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
              {item.invoice_count || 0}
            </span>
          ),
          "Action": (
            <button
              onClick={() => handleViewCustomer(item)}
              className="px-3 py-1 rounded text-sm bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1"
            >
              <EyeOutlined /> View
            </button>
          ),
        }));

        setBody(tableBody);

        setPagination({
          currentPage: 1,
          totalCount: arData.length,
          totalPages: Math.ceil(arData.length / 10),
        });

        setLoader(false);
      } else {
        setLoader(false);
        toast.error("Failed to load AR report data");
      }
    } catch (error) {
      setLoader(false);
      console.error("Failed to load AR report:", error.message);
      toast.error(error.message);
    }
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerInvoices(customer.customer_id);
    setModalVisible(true);
  };

  const fetchCustomerInvoices = async (customerId) => {
    setModalLoading(true);
    try {
      const response = await get_customer_invoices({
        customer_id: customerId,
        include_details: true,
      });

      if (response?.success) {
        setCustomerInvoices(response.data || []);
      } else {
        toast.error("Failed to load customer invoices");
        setCustomerInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching customer invoices:", error);
      toast.error("Failed to load customer invoices");
      setCustomerInvoices([]);
    } finally {
      setModalLoading(false);
    }
  };

  const handleRedirectToCustomer = (customerId) => {
    navigate(`/app/customers/view/${customerId}`);
    setModalVisible(false);
  };

  const headerReset = () => {
    let componentData = [];
    headers?.components?.map((value) => {
      componentData?.push({
        ...value,
        value: null,
      });
    });
    setHeader({ ...headers, components: componentData });
  };

  useEffect(() => {
    fetchData();
    headerReset();
  }, []);

  const exportPDF = async () => {
    try {
      const response = await get_ar_report({
        from: dayjs().startOf("month").format("YYYY-MM-DD"),
        to: dayjs().format("YYYY-MM-DD"),
      });

      if (response?.success) {
        const arData = response.data || [];
        const branch = response.branchInfo;

        const doc = new jsPDF();

        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.text(branch?.name || "Organization Name", pageWidth / 2, 20, {
          align: "center",
        });

        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.text(
          `${branch?.email || ""} | ${branch?.phone || ""} | ${branch?.country || ""}`,
          pageWidth / 2,
          27,
          { align: "center" }
        );

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("Accounts Receivable Report", pageWidth / 2, 40, {
          align: "center",
        });

        autoTable(doc, {
          startY: 50,
          head: [
            [
              "Customer",
              "Total Receivable",
              "Outstanding",
              "0-30 Days",
              "31-60 Days",
              "61-90 Days",
              "90+ Days",
              "Invoice Count",
            ],
          ],
          body: arData.map((item) => [
            item.customer_name,
            (item.total_receivable || 0).toFixed(3),
            (item.outstanding || 0).toFixed(3),
            (item.age_0_30 || 0).toFixed(3),
            (item.age_31_60 || 0).toFixed(3),
            (item.age_61_90 || 0).toFixed(3),
            (item.age_90_plus || 0).toFixed(3),
            item.invoice_count || 0,
          ]),
          theme: "grid",
          styles: { fontSize: 9 },
          headStyles: { fillColor: [24, 144, 255] },
        });

        doc.save(`ar_report_${dayjs().format("YYYYMMDD")}.pdf`);
      }
    } catch (error) {
      console.error("Export PDF error:", error);
      toast.error("Failed to export PDF");
    }
  };

  const exportExcel = async () => {
    try {
      const response = await get_ar_report({
        from: dayjs().startOf("month").format("YYYY-MM-DD"),
        to: dayjs().format("YYYY-MM-DD"),
      });

      if (response?.success) {
        const arData = response.data || [];

        const rows = [];

        rows.push(["ACCOUNTS RECEIVABLE REPORT"]);
        rows.push([]);

        rows.push([
          "Customer",
          "Customer Phone",
          "Total Receivable",
          "Outstanding",
          "0-30 Days",
          "31-60 Days",
          "61-90 Days",
          "90+ Days",
          "Invoice Count",
        ]);

        arData.forEach((item) => {
          rows.push([
            item.customer_name || "",
            item.customer_phone || "",
            Number(item.total_receivable || 0).toFixed(3),
            Number(item.outstanding || 0).toFixed(3),
            Number(item.age_0_30 || 0).toFixed(3),
            Number(item.age_31_60 || 0).toFixed(3),
            Number(item.age_61_90 || 0).toFixed(3),
            Number(item.age_90_plus || 0).toFixed(3),
            item.invoice_count || 0,
          ]);
        });

        const content = rows.map((row) => row.join("\t")).join("\n");

        const blob = new Blob([content], {
          type: "application/vnd.ms-excel",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `ar_report_${dayjs().format("YYYYMMDD")}.xls`;
        link.click();

        URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      console.error("Export Excel error:", error);
      toast.error("Failed to export Excel");
    }
  };

  const invoiceColumns = [
    {
      title: "Invoice Number",
      dataIndex: "number",
      key: "number",
      render: (text) => <span className="font-medium">#INV-{text}</span>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => dayjs(date).format("DD-MMM-YYYY"),
    },
    {
      title: "LPO Number",
      dataIndex: "lpo_number",
      key: "lpo_number",
      render: (text) => text || "-",
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      render: (value) => (
        <span className="font-semibold">{parseFloat(value || 0).toFixed(3)}</span>
      ),
    },
    {
      title: "Paid",
      dataIndex: "paid",
      key: "paid",
      render: (value) => parseFloat(value || 0).toFixed(3),
    },
    {
      title: "Receivable",
      dataIndex: "receivable",
      key: "receivable",
      render: (value) => (
        <span className="text-red-600 font-semibold">
          {parseFloat(value || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status) => {
        const statusMap = {
          0: { text: "Unpaid", color: "red" },
          1: { text: "Partial", color: "orange" },
          2: { text: "Paid", color: "green" },
        };
        const s = statusMap[status] || { text: "Unknown", color: "gray" };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: "Delivery Status",
      dataIndex: "delivery_status",
      key: "delivery_status",
      render: (status) => {
        const statusMap = {
          0: { text: "Pending", color: "red" },
          1: { text: "Partial", color: "orange" },
          2: { text: "Delivered", color: "green" },
        };
        const s = statusMap[status] || { text: "Unknown", color: "gray" };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
  ];

  return (
    <Header
      headers={header}
      setHeaders={setHeader}
      formdata={formdata}
      title="Accounts Receivable Report"
    >
      <div className="flex gap-3 justify-end mt-4">
        <Button
          onClick={exportExcel}
          className="text-medium form-save-button"
          type="primary"
        >
          Export as Excel
        </Button>

        <Button
          onClick={exportPDF}
          className="text-medium form-save-button"
          type="primary"
        >
          Export as PDF
        </Button>
      </div>
      
      <Loadercomponent loader={loader}>
        <Table
          head={tables}
          body={body}
          pagination={pagination}
          paginationdata={paginationdata}
        />
      </Loadercomponent>

      <Modal
        title={
          <div className="flex items-center justify-between">
            <span>
              Invoices - {selectedCustomer?.customer_name || ""}
            </span>
            <Tag color="blue" className="ml-2">
              Total Receivable: {parseFloat(selectedCustomer?.total_receivable || 0).toFixed(3)}
            </Tag>
          </div>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedCustomer(null);
          setCustomerInvoices([]);
        }}
        width={1200}
        footer={[
          <Button
            key="redirect"
            type="primary"
            icon={<FileOutlined />}
            onClick={() => handleRedirectToCustomer(selectedCustomer?.customer_id)}
            disabled={!selectedCustomer?.customer_id}
          >
            View Customer
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setModalVisible(false);
              setSelectedCustomer(null);
              setCustomerInvoices([]);
            }}
          >
            Close
          </Button>,
        ]}
      >
        <div className="mb-4">
          <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="text-sm text-gray-500">Customer</div>
              <div className="font-medium">{selectedCustomer?.customer_name}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Phone</div>
              <div className="font-medium">{selectedCustomer?.customer_phone || "N/A"}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Total Invoices</div>
              <div className="font-medium">{selectedCustomer?.invoice_count || 0}</div>
            </div>
          </div>
          
          <AntTable
            columns={invoiceColumns}
            dataSource={customerInvoices}
            loading={modalLoading}
            pagination={{
              pageSize: 5,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} invoices`,
            }}
            rowKey="_id"
            size="small"
            className="border rounded-lg"
          />
          
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium">Summary</span>
              <div className="flex gap-6">
                <div>
                  <span className="text-sm text-gray-500">Total Invoices:</span>
                  <span className="ml-2 font-semibold">{selectedCustomer?.invoice_count || 0}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Total Receivable:</span>
                  <span className="ml-2 font-semibold text-blue-600">
                    {parseFloat(selectedCustomer?.total_receivable || 0).toFixed(3)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Outstanding:</span>
                  <span className="ml-2 font-semibold text-red-600">
                    {parseFloat(selectedCustomer?.outstanding || 0).toFixed(3)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </Header>
  );
};

export default ArReportTable;